// csv.ts — de-AT-friendly CSV export of a SimulationResult's series + events
// (Szenario-Bibliothek, Feature 3). Semicolon-delimited, NOT comma-delimited: de-AT
// decimal numbers use a comma (formatCentsPlain -> "1.257,31"), which would otherwise
// collide with a comma-separated CSV — semicolon is also what Excel's German/Austrian
// locale expects as the default list separator. Dates use the de-AT dd.mm.yyyy format
// (formatDateDe), matching every other date display in this app.
import type { EngineEvent, SeriesPoint } from '@etf-cockpit/engine';
import { describeEvent } from './events.js';
import { formatCentsPlain, formatDateDe } from './format.js';

const DELIMITER = ';';
const NEWLINE = '\r\n';

function csvEscape(value: string): string {
  if (value.includes(DELIMITER) || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function toCsv(header: string[], rows: string[][]): string {
  return [header, ...rows].map((row) => row.map(csvEscape).join(DELIMITER)).join(NEWLINE);
}

/** One row per SeriesPoint (the Verlauf chart's underlying data). */
export function seriesToCsv(series: SeriesPoint[]): string {
  const header = [
    'Datum',
    'Marktwert',
    'Anschaffungskosten',
    'Verrechnungskonto',
    'Eingezahlt',
    'Gebühren (kum.)',
    'Kursgewinn (kum.)',
    'TER (kum.)',
    'KESt (kum.)',
  ];
  const rows = series.map((p) => [
    formatDateDe(p.date),
    formatCentsPlain(p.valueCents),
    formatCentsPlain(p.basisCents),
    formatCentsPlain(p.settlementCents),
    formatCentsPlain(p.contributedCents),
    formatCentsPlain(p.cumFeesCents),
    formatCentsPlain(p.cumGrossGainCents),
    formatCentsPlain(p.cumTerCents),
    formatCentsPlain(p.cumKestCents),
  ]);
  return toCsv(header, rows);
}

/** One row per engine event (the Ereignisse ledger), reusing lib/events.ts's existing
 * describeEvent normalisation so the CSV matches exactly what EventsLedger shows. */
export function eventsToCsv(events: EngineEvent[]): string {
  const header = ['Datum', 'Typ', 'Betrag', 'KESt', 'Kontostand danach'];
  const rows = events.map((event) => {
    const row = describeEvent(event);
    return [
      formatDateDe(row.date),
      row.typeLabel,
      formatCentsPlain(row.amountCents),
      row.kestCents === null ? '' : formatCentsPlain(row.kestCents),
      formatCentsPlain(row.settlementAfterCents),
    ];
  });
  return toCsv(header, rows);
}
